// @ts-nocheck
import React, { useState, useCallback, useMemo } from 'react';
import { DragDropContext, type DropResult } from '@hello-pangea/dnd';
import { useAppState, useDispatch } from '../store';
import * as api from '../api';
import GroupTable from './GroupTable';
import AddColumnMenu from './AddColumnMenu';
import ShareBoard from './ShareBoard';
import { GROUP_COLORS } from '../types';
import type { Column } from '../types';

export default function BoardView() {
  const state = useAppState();
  const dispatch = useDispatch();
  const [editingName, setEditingName] = useState(false);
  const [boardName, setBoardName] = useState('');
  const [showAddCol, setShowAddCol] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState<{ columnId: string; condition: string; value: string }[]>([]);

  const board = state.boards.find((b) => b.id === state.activeBoardId);
  const groups = [...state.groups].sort((a, b) => a.position - b.position);
  const columns = [...state.columns].sort((a, b) => a.position - b.position);

  const canEdit = state.boardRole === 'editor' || state.boardRole === 'admin';
  const canAdmin = state.boardRole === 'admin';

  const getColumnConfig = (col: Column) => {
    try { return typeof col.config === 'string' ? JSON.parse(col.config) : col.config; }
    catch { return {}; }
  };

  // Filter and search items
  const filteredItemIds = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return new Set(state.items.filter((item) => {
      if (term) {
        const nameMatch = item.name.toLowerCase().includes(term);
        if (!nameMatch) {
          const cellMatch = state.cells.filter((c) => c.item_id === item.id).some((c) => {
            try {
              const val = JSON.parse(c.value);
              if (typeof val === 'string') return val.toLowerCase().includes(term);
              if (typeof val === 'number') return String(val).includes(term);
              if (Array.isArray(val)) return val.some((v) => String(v).toLowerCase().includes(term));
              return JSON.stringify(val).toLowerCase().includes(term);
            } catch { return String(c.value).toLowerCase().includes(term); }
          });
          if (!cellMatch) return false;
        }
      }
      for (const f of filters) {
        if (!f.columnId || (!f.value && f.condition !== 'is_empty' && f.condition !== 'is_not_empty')) continue;
        if (f.columnId === '__name__') {
          const n = item.name.toLowerCase();
          if (f.condition === 'contains' && !n.includes(f.value.toLowerCase())) return false;
          if (f.condition === 'is' && n !== f.value.toLowerCase()) return false;
          if (f.condition === 'is_not' && n === f.value.toLowerCase()) return false;
          if (f.condition === 'not_contains' && n.includes(f.value.toLowerCase())) return false;
          continue;
        }
        const cell = state.cells.find((c) => c.item_id === item.id && c.column_id === f.columnId);
        let cv = '';
        if (cell) { try { const p = JSON.parse(cell.value); cv = typeof p === 'string' ? p : JSON.stringify(p); } catch { cv = cell.value; } }
        const cvl = cv.toLowerCase(), fvl = f.value.toLowerCase();
        if (f.condition === 'is' && cvl !== fvl) return false;
        if (f.condition === 'is_not' && cvl === fvl) return false;
        if (f.condition === 'contains' && !cvl.includes(fvl)) return false;
        if (f.condition === 'not_contains' && cvl.includes(fvl)) return false;
        if (f.condition === 'is_empty' && cv !== '' && cv !== '""') return false;
        if (f.condition === 'is_not_empty' && (cv === '' || cv === '""')) return false;
      }
      return true;
    }).map((i) => i.id));
  }, [state.items, state.cells, searchTerm, filters]);

  const isFiltering = searchTerm.trim() !== '' || filters.some((f) => f.columnId && (f.value || f.condition === 'is_empty' || f.condition === 'is_not_empty'));
  const activeFilterCount = filters.filter((f) => f.columnId).length;

  const handleBoardNameSave = async () => {
    if (!board || !boardName.trim()) return;
    await api.updateBoard(board.id, { name: boardName.trim() });
    dispatch({ type: 'SET_BOARDS', boards: state.boards.map((b) => b.id === board.id ? { ...b, name: boardName.trim() } : b) });
    setEditingName(false);
  };

  const handleAddGroup = async () => {
    if (!board) return;
    const maxPos = groups.length > 0 ? Math.max(...groups.map((g) => g.position)) : 0;
    const color = GROUP_COLORS[(groups.length) % GROUP_COLORS.length];
    const group = await api.createGroup(board.id, 'New Group', color, maxPos + 1000);
    dispatch({ type: 'ADD_GROUP', group });
  };

  const handleNewItem = async () => {
    if (!board || groups.length === 0) return;
    const firstGroup = groups[0];
    const groupItems = state.items.filter((i) => i.group_id === firstGroup.id);
    const maxPos = groupItems.length > 0 ? Math.max(...groupItems.map((i) => i.position)) : 0;
    const item = await api.createItem(firstGroup.id, board.id, 'New Item', maxPos + 1000);
    dispatch({ type: 'ADD_ITEM', item });
  };

  const addFilter = () => setFilters([...filters, { columnId: '', condition: 'contains', value: '' }]);
  const updateFilter = (i: number, d: Partial<{ columnId: string; condition: string; value: string }>) =>
    setFilters(filters.map((f, idx) => idx === i ? { ...f, ...d } : f));
  const removeFilter = (i: number) => setFilters(filters.filter((_, idx) => idx !== i));
  const clearAll = () => { setSearchTerm(''); setFilters([]); setShowFilter(false); };

  const getColumnValues = (colId: string): string[] => {
    const col = columns.find((c) => c.id === colId);
    if (!col) return [];
    const config = getColumnConfig(col);
    if (col.type === 'status' || col.type === 'priority')
      return Object.entries(config.labels || {}).filter(([k]) => k !== '').map(([k]) => k);
    const vals = new Set<string>();
    state.cells.filter((c) => c.column_id === colId).forEach((c) => {
      try { const v = JSON.parse(c.value); if (typeof v === 'string' && v) vals.add(v); } catch {}
    });
    return Array.from(vals).slice(0, 50);
  };

  const getFilterLabel = (colId: string, val: string): string => {
    const col = columns.find((c) => c.id === colId);
    if (!col) return val;
    const config = getColumnConfig(col);
    if ((col.type === 'status' || col.type === 'priority') && config.labels?.[val]) return config.labels[val];
    return val;
  };

  const onDragEnd = useCallback(async (result: DropResult) => {
    if (!canEdit) return;
    const { source, destination, draggableId, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    if (type === 'ITEM') {
      const dstGroupId = destination.droppableId;
      const dstItems = state.items.filter((i) => i.group_id === dstGroupId).sort((a, b) => a.position - b.position);
      const filteredDst = dstItems.filter((i) => i.id !== draggableId);
      let newPos: number;
      const dstIdx = destination.index;
      if (filteredDst.length === 0) newPos = 1000;
      else if (dstIdx === 0) newPos = filteredDst[0].position - 1000;
      else if (dstIdx >= filteredDst.length) newPos = filteredDst[filteredDst.length - 1].position + 1000;
      else newPos = (filteredDst[dstIdx - 1].position + filteredDst[dstIdx].position) / 2;
      dispatch({ type: 'UPDATE_ITEM', id: draggableId, data: { group_id: dstGroupId, position: newPos } });
      await api.updateItem(draggableId, { group_id: dstGroupId, position: newPos });
    }
  }, [state.items, dispatch, canEdit]);

  if (!board) return null;

  return (
    <div className="board-view">
      <div className="board-sticky-top">
      {/* Board name row */}
      <div style={{ marginBottom: 4 }}>
        {canAdmin && editingName ? (
          <input className="board-name" autoFocus value={boardName}
            onChange={(e) => setBoardName(e.target.value)} onBlur={handleBoardNameSave}
            onKeyDown={(e) => { if (e.key === 'Enter') handleBoardNameSave(); if (e.key === 'Escape') setEditingName(false); }} />
        ) : (
          <h1 className="board-name" onClick={() => { if (canAdmin) { setBoardName(board.name); setEditingName(true); } }}
            style={{ cursor: canAdmin ? 'pointer' : 'default' }}>
            {board.name}
          </h1>
        )}
      </div>

      {/* ─── Monday-style toolbar ─── */}
      <div className="board-toolbar">
        {canEdit && (
          <button className="tb-new-item" onClick={handleNewItem}>
            New item
          </button>
        )}

        <div className="tb-search-box">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search this board" className="tb-search-input" />
          {searchTerm && <button className="tb-clear" onClick={() => setSearchTerm('')}>&#10005;</button>}
        </div>

        <button className={`tb-btn ${showFilter ? 'active' : ''}`} onClick={() => setShowFilter(!showFilter)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
          Filter{activeFilterCount > 0 && <span className="tb-badge">{activeFilterCount}</span>}
        </button>

        <button className="tb-btn" onClick={() => setShowShare(true)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
          Share
        </button>

        {canEdit && (
          <>
            <button className="tb-btn" onClick={handleAddGroup}>+ Group</button>
            <div style={{ position: 'relative' }}>
              <button className="tb-btn" onClick={() => setShowAddCol(!showAddCol)}>+ Column</button>
              {showAddCol && <AddColumnMenu boardId={board.id} onClose={() => setShowAddCol(false)} />}
            </div>
          </>
        )}

        {isFiltering && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#676879' }}>
            {filteredItemIds.size} of {state.items.length} items
            <button onClick={clearAll} style={{ marginLeft: 6, fontSize: 12, color: '#0073ea', background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>
          </span>
        )}
      </div>
      </div>

      {/* Filter panel */}
      {showFilter && (
        <div className="filter-panel">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              Advanced filters
              <span style={{ fontWeight: 400, color: '#676879', fontSize: 12, marginLeft: 8 }}>
                Showing {isFiltering ? filteredItemIds.size : state.items.length} of {state.items.length} items
              </span>
            </span>
            {filters.length > 0 && (
              <button onClick={clearAll} style={{ fontSize: 13, color: '#676879', background: 'none', border: 'none', cursor: 'pointer' }}>Clear all</button>
            )}
          </div>
          {filters.map((f, idx) => (
            <div key={idx} className="filter-row">
              <span className="filter-label">{idx === 0 ? 'Where' : 'And'}</span>
              <select value={f.columnId} onChange={(e) => updateFilter(idx, { columnId: e.target.value, value: '' })} className="filter-select">
                <option value="">Column</option>
                <option value="__name__">Item Name</option>
                {columns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={f.condition} onChange={(e) => updateFilter(idx, { condition: e.target.value })} className="filter-select">
                <option value="contains">contains</option>
                <option value="not_contains">doesn't contain</option>
                <option value="is">is</option>
                <option value="is_not">is not</option>
                <option value="is_empty">is empty</option>
                <option value="is_not_empty">is not empty</option>
              </select>
              {f.condition !== 'is_empty' && f.condition !== 'is_not_empty' && (
                f.columnId && (columns.find((c) => c.id === f.columnId)?.type === 'status' || columns.find((c) => c.id === f.columnId)?.type === 'priority') ? (
                  <select value={f.value} onChange={(e) => updateFilter(idx, { value: e.target.value })} className="filter-select" style={{ flex: 1 }}>
                    <option value="">Value</option>
                    {getColumnValues(f.columnId).map((v) => <option key={v} value={v}>{getFilterLabel(f.columnId, v)}</option>)}
                  </select>
                ) : (
                  <input value={f.value} onChange={(e) => updateFilter(idx, { value: e.target.value })} placeholder="Value" className="filter-input" />
                )
              )}
              <button onClick={() => removeFilter(idx)} className="filter-remove">&#10005;</button>
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <button onClick={addFilter} className="filter-add">+ New filter</button>
          </div>
        </div>
      )}

      {/* Groups */}
      <DragDropContext onDragEnd={onDragEnd}>
        {groups.map((group) => {
          const groupItems = state.items.filter((i) => i.group_id === group.id).sort((a, b) => a.position - b.position);
          const visibleItems = isFiltering ? groupItems.filter((i) => filteredItemIds.has(i.id)) : groupItems;
          if (isFiltering && visibleItems.length === 0) return null;
          return <GroupTable key={group.id} group={group} columns={columns} items={visibleItems} readOnly={!canEdit} />;
        })}
      </DragDropContext>

      {groups.length === 0 && (
        <div className="empty-state" style={{ padding: 60 }}>
          <p>{canEdit ? 'No groups yet. Click "+ Group" to add one.' : 'This board has no content yet.'}</p>
        </div>
      )}

      {showShare && <ShareBoard boardId={board.id} onClose={() => setShowShare(false)} />}
    </div>
  );
}
